import { duration } from 'src/replicant/utils/duration';
import type { PetSpeciesID } from '../pet';
import { AvatarFrameId } from '../profile/frames';

export type PlayerID = string;
export type FriendshipRequestStatus = 'pending' | 'accepted' | 'rejected';

/**
 * Canonical `method` values for friends analytics. Used by
 * `getFriendshipShareMethod` (entry-subFeature lookup) and
 * `getFriendshipRequestMethod` (state-mirror) in `friends.getters.ts`.
 *
 * Defined independently of the analytics schema's `friendMethods` tuple in
 * `friends.analytics.schema.ts` — the two are intentionally never
 * cross-imported.
 */
export type FriendShareMethod =
  | 'request'
  | 'line_share'
  | 'native_share'
  | 'qr_code'
  | 'unknown';

// Playing friend profile
export type PlayerProfile = {
  id: PlayerID;
  name: string;
  profilePicture: string;
  frame?: AvatarFrameId;
  species: PetSpeciesID;
};

// Raw player entry data from server for new UI
export type PlayerEntryData = {
  updatedAt: number;
  id: string;
  name: string;
  photo: string;
  level: number;
  avatarFrame: AvatarFrameId;
  petSpecies: PetSpeciesID;
  petName: string;
  petState: string;
  isFriend: boolean;
  isOnline: boolean;
  canVisit: boolean;
  teamName?: string;
  teamIcon?: string;
  originSubFeature?: 'friend_list_cluster' | 'friend_list_suggestions';
};

// Generic friendship request type
export type FriendshipRequest = {
  target: PlayerProfile;
  owner: PlayerProfile;
  status: FriendshipRequestStatus;
  createdAt: number;
  updatedAt: number;
  sourceFeature?: string;
  originFeature?: string;
  originSubFeature?: string;
  method?: string;
  receivedAt?: number;
  senderAcknowledgedAt?: number;
};

/** External friend profile for GCInstant setExternalFriendsGetter and updateExternalFriends */
export type ExternalFriend = {
  id: string;
  isPlayer: boolean;
  profilePicture: string;
  frame: AvatarFrameId;
  name: string;
};

// Clear friendship requests after 30 days
export const clearFriendshipRequestsAfter = duration({ days: 30 });

// Friend list limit
export const friendListLimit = 250;
